import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useT } from '../i18n/i18n';

type Props = {
  open: boolean;
  /**
   * Verify the entered PIN. Resolve to true on success, false on a wrong PIN.
   * Throw to surface a transport / network error in the dialog.
   */
  onVerify: (pin: string) => Promise<boolean>;
  /**
   * Optional: verify the user's account password and clear the supervisor PIN.
   * Resolve to true on success, false on wrong password. If omitted, the
   * "Forgot PIN?" link is hidden.
   */
  onForgotSubmit?: (password: string) => Promise<boolean>;
  /** Called after a successful verify/forgot. */
  onSuccess: () => void;
  /** Called when the supervisor cancels. */
  onCancel: () => void;
  title?: string;
  message?: string;
};

const PIN_LEN = 4;
type Mode = 'pin' | 'forgot';

export default function PinDialog({
  open,
  onVerify,
  onForgotSubmit,
  onSuccess,
  onCancel,
  title,
  message,
}: Props) {
  const t = useT();
  const titleId = useId();
  const descId = useId();
  const [mode, setMode] = useState<Mode>('pin');
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pinInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);

  // Reset internal state on each open and focus the right input.
  useEffect(() => {
    if (!open) return;
    setMode('pin');
    setPin('');
    setPassword('');
    setError(null);
    setBusy(false);
    const t = window.setTimeout(() => pinInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  // Focus the active input on mode change.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      if (mode === 'pin') pinInputRef.current?.focus();
      else passwordInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [mode, open]);

  // ESC cancels.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  async function attemptPin(value: string) {
    setBusy(true);
    setError(null);
    try {
      const ok = await onVerify(value);
      if (ok) {
        onSuccess();
      } else {
        setError(t('Incorrect PIN. Try again.'));
        setPin('');
        pinInputRef.current?.focus();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not verify PIN.'));
    } finally {
      setBusy(false);
    }
  }

  async function attemptPassword() {
    if (!onForgotSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const ok = await onForgotSubmit(password);
      if (ok) {
        onSuccess();
      } else {
        setError(t('Account password is incorrect.'));
        setPassword('');
        passwordInputRef.current?.focus();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not verify password.'));
    } finally {
      setBusy(false);
    }
  }

  function handlePinChange(e: React.ChangeEvent<HTMLInputElement>) {
    const cleaned = e.target.value.replace(/\D/g, '').slice(0, PIN_LEN);
    setPin(cleaned);
    if (cleaned.length === PIN_LEN && !busy) {
      void attemptPin(cleaned);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (mode === 'pin') {
      if (pin.length === PIN_LEN) void attemptPin(pin);
    } else {
      if (password) void attemptPassword();
    }
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descId}>
        <h2 id={titleId} className="modal-title">
          {mode === 'pin' ? title ?? t('Supervisor PIN required') : t('Reset with account password')}
        </h2>
        <p id={descId} className="modal-body">
          {mode === 'pin'
            ? message ?? t('Enter the supervisor PIN to leave the game.')
            : t('Enter your account password to clear the supervisor PIN and leave the game. You can set a new PIN afterwards in Settings.')}
        </p>
        <form onSubmit={submit} className="spaced">
          {mode === 'pin' ? (
            <div className="field">
              <label htmlFor="pin-input" className="field-hint">
                {t('4-digit PIN')}
              </label>
              <input
                id="pin-input"
                ref={pinInputRef}
                type="password"
                inputMode="numeric"
                autoComplete="off"
                pattern="\d{4}"
                maxLength={PIN_LEN}
                value={pin}
                onChange={handlePinChange}
                disabled={busy}
                className="pin-input"
              />
            </div>
          ) : (
            <div className="field">
              <label htmlFor="pin-password-input" className="field-hint">
                {t('Account password')}
              </label>
              <input
                id="pin-password-input"
                ref={passwordInputRef}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </div>
          )}

          {error && <div className="error" role="alert">{error}</div>}

          <div className="modal-actions">
            {onForgotSubmit && (
              <button
                type="button"
                className="btn ghost"
                onClick={() => { setError(null); setMode(mode === 'pin' ? 'forgot' : 'pin'); }}
                disabled={busy}
                style={{ marginRight: 'auto' }}
              >
                {mode === 'pin' ? t('Forgot PIN?') : t('Use PIN instead')}
              </button>
            )}
            <button type="button" className="btn secondary" onClick={onCancel} disabled={busy}>
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="btn"
              disabled={busy || (mode === 'pin' ? pin.length !== PIN_LEN : !password)}
            >
              {busy ? t('Checking…') : t('Continue')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
